import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import App from './App';
import './styles/index.css';
import './styles/loader.css';
import './i18n';
import { useUIStore } from './store/ui.store';
import { setupServiceWorkerNotificationRouting } from './utils/notifications';
import { registerWebPushSubscription } from './services/push.service';
import { dismissInitialLoader } from './utils/dismissInitialLoader';
import { bootstrapApiRouting } from './config/api.config';

// Domain lock: production SPA host tropayment.com
const PRODUCTION_APP_HOSTS = [
  'tropayment.com',
  'www.tropayment.com',
] as const;

function isAllowedAppHost(host: string): boolean {
  const h = host.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') {
    return true;
  }
  return PRODUCTION_APP_HOSTS.some(
    (allowed) => h === allowed || h.endsWith('.' + allowed.replace(/^www\./, ''))
  );
}

if (import.meta.env.PROD) {
  const host = window.location.hostname.toLowerCase();
  if (!isAllowedAppHost(host)) {
    window.location.replace('https://tropayment.com' + window.location.pathname + window.location.search);
    throw new Error('Redirecting');
  }
}

// Initialize theme before render to prevent flash
useUIStore.getState().initTheme();

// Register PWA only after user interaction or long delay — avoids workbox on LCP critical path
function scheduleServiceWorkerRegistration() {
  let done = false;
  const register = () => {
    if (done) return;
    done = true;
    import('virtual:pwa-register').then(({ registerSW }) => {
      registerSW({
        immediate: false,
        onRegisteredSW(_swUrl?: string, registration?: ServiceWorkerRegistration) {
          if (registration) {
            const scopePath = new URL(registration.scope).pathname;
            if (import.meta.env.DEV && scopePath !== '/') {
              console.warn('[PWA] Service worker scope is not root:', registration.scope);
            }
          }
        },
      });
    });
  };

  const onFirstInteraction = () => {
    interactionEvents.forEach((e) => window.removeEventListener(e, onFirstInteraction, listenerOpts));
    register();
  };
  const interactionEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
  const listenerOpts = { passive: true, capture: true } as const;
  interactionEvents.forEach((e) => window.addEventListener(e, onFirstInteraction, listenerOpts));

  window.addEventListener(
    'load',
    () => {
      window.setTimeout(register, 60_000);
    },
    { once: true }
  );
}

function isAdminAppPath(): boolean {
  const p = window.location.pathname;
  return p === '/admin' || p.startsWith('/admin/');
}

if (import.meta.env.PROD && !isAdminAppPath()) {
  scheduleServiceWorkerRegistration();
} else if (!import.meta.env.PROD && !isAdminAppPath()) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_swUrl?: string, registration?: ServiceWorkerRegistration) {
        if (registration) {
          const scopePath = new URL(registration.scope).pathname;
          if (scopePath !== '/') {
            console.warn('[PWA] Service worker scope is not root:', registration.scope);
          }
        }
      },
    });
  });
}

// Re-register merchant Web Push when the browser rotates subscription keys (admin uses admin-sw.js)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      registerWebPushSubscription().catch(() => {});
    }
  });
}

// PWA standalone: try to minimize URL bar visibility (iOS Safari)
if (typeof window !== 'undefined') {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;
  if (isStandalone) {
    document.documentElement.classList.add('pwa-standalone');
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function NotificationRoutingBridge({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    return setupServiceWorkerNotificationRouting((url) => {
      try {
        const path = new URL(url, window.location.origin).pathname + new URL(url, window.location.origin).search;
        navigate(path);
      } catch {
        navigate(url.startsWith('/') ? url : '/dashboard');
      }
    });
  }, [navigate]);

  return <>{children}</>;
}

if (import.meta.env.PROD) {
  void bootstrapApiRouting();
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NotificationRoutingBridge>
          <App />
        </NotificationRoutingBridge>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

requestAnimationFrame(() => {
  requestAnimationFrame(() => dismissInitialLoader());
});

